import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Trash2 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const API = 'https://api.glowytics.com/api/instagram-radar';

/* ── Helpers ───────────────────────────────────────────────────────── */

const proxyImg = (url) => url ? `${API}/image-proxy?url=${encodeURIComponent(url)}` : null;
const format = (n) => Number(n || 0).toLocaleString('en-US');
const formatNumber = (n) => {
  if (n === null || n === undefined) return '-';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return Number(n).toLocaleString('en-US');
};
const formatDate = (ts) => {
  if (!ts) return '-';
  const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};
const formatShortDate = (s) => {
  if (!s) return '-';
  const d = new Date(s);
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};
const timeAgo = (ts) => {
  if (!ts) return '';
  const sec = typeof ts === 'number' ? ts : Math.floor(new Date(ts).getTime() / 1000);
  const diff = Math.floor(Date.now() / 1000) - sec;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
};
const mediaTypeLabel = (type, productType) => {
  if (productType === 'clips') return { text: 'Reel', color: 'bg-purple-50 text-purple-700 border-purple-200' };
  if (type === 8) return { text: 'Carousel', color: 'bg-blue-50 text-blue-700 border-blue-200' };
  if (type === 2) return { text: 'Video', color: 'bg-amber-50 text-amber-700 border-amber-200' };
  return { text: 'Photo', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
};
const getBrandId = () => {
  try { const c = localStorage.getItem('currentBrandId') || sessionStorage.getItem('currentBrandId'); if (c) return c; } catch (_) {}
  for (const k of ['brandId', 'selectedBrandId', 'activeBrandId', 'brand_id']) { try { const v = localStorage.getItem(k) || sessionStorage.getItem(k); if (v) return v; } catch (_) {} }
  for (const k of ['selectedBrand', 'currentBrand', 'activeBrand']) { try { const r = localStorage.getItem(k) || sessionStorage.getItem(k); if (r) { const o = JSON.parse(r); if (o?.id) return o.id; } } catch (_) {} }
  try { const m = window.location.pathname.match(/[0-9a-fA-F-]{36}/); if (m) return m[0]; } catch (_) {}
  return '';
};

/* ── Analytics ─────────────────────────────────────────────────────── */

function getEngagementGrade(rate) {
  if (rate == null) return { grade: '?', color: 'text-slate-400', bg: 'bg-slate-50', border: 'border-slate-200', label: 'N/A' };
  if (rate >= 6)   return { grade: 'A+', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Exceptional' };
  if (rate >= 3.5) return { grade: 'A', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Excellent' };
  if (rate >= 2)   return { grade: 'B+', color: 'text-teal-600', bg: 'bg-teal-50', border: 'border-teal-200', label: 'Very Good' };
  if (rate >= 1.5) return { grade: 'B', color: 'text-sky-600', bg: 'bg-sky-50', border: 'border-sky-200', label: 'Good' };
  if (rate >= 1)   return { grade: 'C+', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Average' };
  if (rate >= 0.5) return { grade: 'C', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', label: 'Below Avg' };
  if (rate >= 0.2) return { grade: 'D', color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-200', label: 'Poor' };
  return { grade: 'F', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', label: 'Very Poor' };
}

function getEstimatedEarnings(followers, engRate) {
  if (!followers || followers < 1000) return null;
  let baseCPM = 8;
  if (followers >= 1_000_000) baseCPM = 20;
  else if (followers >= 500_000) baseCPM = 15;
  else if (followers >= 50_000) baseCPM = 12;
  else if (followers >= 10_000) baseCPM = 10;
  const m = engRate ? Math.max(0.5, Math.min(3, engRate / 1.5)) : 1;
  const base = (followers / 1000) * baseCPM * m;
  return { min: Math.round(base * 0.6), max: Math.round(base * 1.4) };
}

function getContentBreakdown(posts) {
  const map = {
    reel: { count: 0, label: 'Reel', bg: 'bg-slate-800' },
    photo: { count: 0, label: 'Photo', bg: 'bg-slate-500' },
    carousel: { count: 0, label: 'Carousel', bg: 'bg-slate-400' },
    video: { count: 0, label: 'Video', bg: 'bg-slate-300' },
  };
  for (const p of posts) {
    if (p.product_type === 'clips') map.reel.count++;
    else if (p.media_type === 8) map.carousel.count++;
    else if (p.media_type === 2) map.video.count++;
    else map.photo.count++;
  }
  const total = posts.length || 1;
  return Object.entries(map).map(([key, v]) => ({ ...v, key, pct: Math.round((v.count / total) * 100) })).filter(t => t.count > 0).sort((a, b) => b.count - a.count);
}

function getPostingFrequency(posts) {
  if (posts.length < 2) return { perWeek: 0, perMonth: 0 };
  const dates = posts.map(p => { if (!p.taken_at) return 0; return typeof p.taken_at === 'number' ? p.taken_at : Math.floor(new Date(p.taken_at).getTime() / 1000); }).filter(Boolean).sort((a, b) => a - b);
  if (dates.length < 2) return { perWeek: 0, perMonth: 0 };
  const spanDays = (dates[dates.length - 1] - dates[0]) / 86400;
  if (spanDays < 1) return { perWeek: posts.length, perMonth: posts.length };
  const perDay = dates.length / spanDays;
  return { perWeek: Math.round(perDay * 7 * 10) / 10, perMonth: Math.round(perDay * 30) };
}

function getBestPostingHours(posts) {
  const hours = Array.from({ length: 24 }, (_, i) => ({ hour: i, count: 0, totalEng: 0, avgEng: 0 }));
  for (const p of posts) {
    if (!p.taken_at) continue;
    const ts = typeof p.taken_at === 'number' ? p.taken_at : Math.floor(new Date(p.taken_at).getTime() / 1000);
    const h = new Date(ts * 1000).getHours();
    hours[h].count++;
    hours[h].totalEng += (p.like_count || 0) + (p.comment_count || 0);
  }
  hours.forEach(h => { h.avgEng = h.count > 0 ? Math.round(h.totalEng / h.count) : 0; });
  return hours;
}

function getTopPosts(posts, n = 6) {
  return [...posts].sort((a, b) => ((b.like_count || 0) + (b.comment_count || 0)) - ((a.like_count || 0) + (a.comment_count || 0))).slice(0, n);
}

function computeDailyChanges(dailyStats) {
  if (!dailyStats || dailyStats.length === 0) return [];
  const sorted = [...dailyStats].sort((a, b) => new Date(a.stat_date) - new Date(b.stat_date));
  return sorted.map((s, i) => {
    const prev = i > 0 ? sorted[i - 1] : null;
    const fc = Number(s.follower_count) || 0, fwc = Number(s.following_count) || 0, mc = Number(s.media_count) || 0;
    return {
      date: s.stat_date, followers: fc, following: fwc, media: mc,
      followerChange: prev ? fc - (Number(prev.follower_count) || 0) : 0,
      followingChange: prev ? fwc - (Number(prev.following_count) || 0) : 0,
      mediaChange: prev ? mc - (Number(prev.media_count) || 0) : 0,
    };
  }).reverse();
}

/* ── Sub-components ────────────────────────────────────────────────── */

const InstagramIcon = ({ className = 'w-7 h-7' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

function MiniLineChart({ data, height = 120, color = '#0ea5e9' }) {
  if (!data || data.length < 2) return <div className="h-20 flex items-center justify-center text-xs text-slate-400">Not enough data</div>;
  const chartData = data.map(d => {
    const dt = new Date(d.date);
    return { date: d.date, value: d.value, label: dt.toLocaleDateString('en-US', { day: '2-digit', month: 'short' }) };
  });
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
        <defs>
          <linearGradient id="followerGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} interval="equidistantPreserveStart" />
        <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} domain={['dataMin - 100', 'dataMax + 100']} />
        <Tooltip
          contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
          formatter={(value) => [Number(value).toLocaleString(), 'Followers']}
          labelFormatter={(label) => label}
        />
        <Area type="monotone" dataKey="value" stroke={color} strokeWidth={2} fill="url(#followerGrad)" dot={false} activeDot={{ r: 4, fill: color, strokeWidth: 0 }} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="w-0.5 h-4 rounded-full bg-slate-300" />
      <span className="text-[13px] font-semibold text-slate-800 tracking-tight">{children}</span>
    </div>
  );
}

function PostingHoursChart({ hours }) {
  const maxEng = Math.max(...hours.map(h => h.avgEng), 1);
  if (!hours.some(h => h.count > 0)) return <div className="text-xs text-slate-400 text-center py-4">No posting data</div>;
  return (
    <div>
      <div className="flex items-end gap-px h-16 mb-1">
        {hours.map(h => (
          <div key={h.hour} className="flex-1 group relative">
            <div className="w-full rounded-t-sm" style={{ height: h.count > 0 ? `${Math.max(12, (h.avgEng / maxEng) * 100)}%` : '0', backgroundColor: h.count > 0 ? `rgba(14,165,233,${0.2 + (h.avgEng / maxEng) * 0.6})` : 'transparent' }} />
            {h.count > 0 && (
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-[9px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                {String(h.hour).padStart(2, '0')}:00 · {h.count} posts · avg {formatNumber(h.avgEng)} eng
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-px text-[8px] text-slate-400">
        {hours.map(h => <div key={h.hour} className="flex-1 text-center">{h.hour % 4 === 0 ? String(h.hour).padStart(2, '0') : ''}</div>)}
      </div>
    </div>
  );
}

/* ── Main ──────────────────────────────────────────────────────────── */

export default function InstagramRadar() {
  const [accounts, setAccounts] = useState([]);
  const [query, setQuery] = useState('');
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [deleteState, setDeleteState] = useState({ open: false, step: 1, account: null });
  const [selectedId, setSelectedId] = useState(null);
  const [profileData, setProfileData] = useState({});
  const [profileLoading, setProfileLoading] = useState({});
  const [viewMode, setViewMode] = useState('grid');
  const [showAllDays, setShowAllDays] = useState(false);
  const previewRef = useRef(null);
  const [postPopup, setPostPopup] = useState(null);

  const loadAccounts = async () => {
    try {
      const brandId = getBrandId(); if (!brandId) return;
      const resp = await fetch(`${API}/accounts`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) return;
      const data = await resp.json();
      setAccounts(data?.accounts || []);
    } catch (_) {}
  };
  useEffect(() => { loadAccounts(); }, []);

  const filtered = accounts.filter(a =>
    (a.full_name || '').toLowerCase().includes(query.toLowerCase()) ||
    (a.username || '').toLowerCase().includes(query.toLowerCase())
  );

  const addAccount = async () => {
    setErrorMsg('');
    if (!newUsername.trim()) { setErrorMsg('Please enter a username or URL'); return; }
    const brandId = getBrandId();
    if (!brandId) { setErrorMsg('Brand not selected'); return; }
    setIsSaving(true);
    try {
      const resp = await fetch(`${API}/account/add`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }, credentials: 'include', body: JSON.stringify({ usernameInput: newUsername.trim(), brandId }) });
      const data = await resp.json();
      if (!resp.ok || !data?.ok) throw new Error(data?.error || 'Failed');
      await loadAccounts(); setNewUsername(''); setIsAddOpen(false);
    } catch (e) { setErrorMsg(e?.message || 'Failed to add account'); }
    finally { setIsSaving(false); }
  };

  const requestDelete = (account) => setDeleteState({ open: true, step: 1, account });
  const performDelete = async () => {
    if (!deleteState?.account) return;
    const brandId = getBrandId(); if (!brandId) return;
    try {
      setIsSaving(true);
      const resp = await fetch(`${API}/account/${deleteState.account.id}`, { method: 'DELETE', headers: { 'X-Brand-ID': brandId }, credentials: 'include' });
      if (!resp.ok) throw new Error('Delete failed');
      setAccounts(prev => prev.filter(a => a.id !== deleteState.account.id));
      if (selectedId === deleteState.account.id) setSelectedId(null);
      setDeleteState({ open: false, step: 1, account: null });
    } catch (e) { setErrorMsg(e?.message || 'Delete failed'); }
    finally { setIsSaving(false); }
  };

  const selectAccount = (account) => {
    const newId = selectedId === account.id ? null : account.id;
    setSelectedId(newId); setShowAllDays(false);
    if (newId) { fetchProfileForAccount(newId, account); setTimeout(() => previewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100); }
  };

  const fetchProfileForAccount = async (accountId, account) => {
    if (profileData[accountId]) return;
    const brandId = getBrandId();
    const dbProfile = {
      username: account.username, full_name: account.full_name, biography: account.biography || null,
      follower_count: Number(account.follower_count) || 0, following_count: Number(account.following_count) || 0,
      media_count: Number(account.media_count) || 0, is_verified: account.is_verified, is_business: account.is_business,
      is_private: false, profile_pic_url: account.profile_pic_url, external_url: null,
    };
    setProfileData(prev => ({ ...prev, [accountId]: { profile: dbProfile, content: [], stats: null, source: 'db' } }));
    setProfileLoading(prev => ({ ...prev, [accountId]: true }));
    try {
      const [postsResp, growthResp, dailyResp] = await Promise.all([
        fetch(`${API}/account/${accountId}/posts`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
        fetch(`${API}/account/${accountId}/growth`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
        fetch(`${API}/account/${accountId}/daily-stats`, { headers: { 'X-Brand-ID': brandId }, credentials: 'include' }),
      ]);
      let growth = null;
      if (growthResp.ok) { const g = await growthResp.json(); if (g?.ok) growth = g; }
      let dailyStats = [];
      if (dailyResp.ok) { const d = await dailyResp.json(); if (d?.ok) dailyStats = d.stats || []; }
      if (postsResp.ok) {
        const data = await postsResp.json();
        const posts = (data?.posts || []).map(p => ({
          code: p.post_code,
          url: p.post_code ? `https://www.instagram.com/p/${p.post_code}/` : undefined,
          media_type: p.media_type, product_type: p.product_type, caption: p.caption,
          like_count: Number(p.like_count) || 0, comment_count: Number(p.comment_count) || 0,
          play_count: p.play_count != null && p.play_count !== '' ? Number(p.play_count) : null,
          share_count: p.share_count != null && p.share_count !== '' ? Number(p.share_count) : null,
          video_duration: p.video_duration ? Number(p.video_duration) : null, video_url: p.video_url || null,
          thumbnail_url: p.thumbnail_url, taken_at: p.taken_at,
        }));
        const totalLikes = posts.reduce((s, c) => s + (c.like_count || 0), 0);
        const totalComments = posts.reduce((s, c) => s + (c.comment_count || 0), 0);
        const totalPlays = posts.filter(c => c.play_count).reduce((s, c) => s + c.play_count, 0);
        const totalShares = posts.reduce((s, c) => s + (c.share_count != null ? Number(c.share_count) : 0), 0);
        const avgLikes = posts.length > 0 ? Math.round(totalLikes / posts.length) : 0;
        const avgShares = posts.length > 0 ? Math.round(totalShares / posts.length) : 0;
        const fc = Number(account.follower_count) || 0;
        const engagementRate = fc > 0 && posts.length > 0 ? parseFloat(((totalLikes + totalComments) / posts.length / fc * 100).toFixed(3)) : null;
        setProfileData(prev => ({ ...prev, [accountId]: { profile: dbProfile, content: posts, stats: { total_content: posts.length, total_likes: totalLikes, total_comments: totalComments, total_plays: totalPlays, total_shares: totalShares, avg_likes: avgLikes, avg_shares: avgShares, engagement_rate: engagementRate }, growth, dailyStats, source: 'db' } }));
      }
    } catch (_) {}
    finally { setProfileLoading(prev => ({ ...prev, [accountId]: false })); }
  };

  /* ── Computed ──────────────────────────────────────────────────── */
  const selectedAccount = accounts.find(a => a.id === selectedId);
  const pd = selectedId ? profileData[selectedId] : null;
  const isLoadingPreview = selectedId ? !!profileLoading[selectedId] : false;
  const profile = pd?.profile;
  const content = pd?.content || [];
  const stats = pd?.stats;
  const growth = pd?.growth;
  const dailyStats = pd?.dailyStats || [];

  const grade = useMemo(() => getEngagementGrade(stats?.engagement_rate), [stats]);
  const earnings = useMemo(() => getEstimatedEarnings(profile?.follower_count, stats?.engagement_rate), [profile, stats]);
  const breakdown = useMemo(() => getContentBreakdown(content), [content]);
  const frequency = useMemo(() => getPostingFrequency(content), [content]);
  const postingHours = useMemo(() => getBestPostingHours(content), [content]);
  const topPosts = useMemo(() => getTopPosts(content), [content]);
  const dailyChanges = useMemo(() => computeDailyChanges(dailyStats), [dailyStats]);
  const bestHours = useMemo(() => [...postingHours].filter(h => h.count > 0).sort((a, b) => b.avgEng - a.avgEng).slice(0, 3), [postingHours]);
  const chartData = useMemo(() => {
    if (!dailyStats || dailyStats.length < 2) return [];
    return [...dailyStats].sort((a, b) => new Date(a.stat_date) - new Date(b.stat_date)).map(s => ({ date: s.stat_date, value: Number(s.follower_count) || 0 }));
  }, [dailyStats]);

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">

        {/* ═══ Account List ═══ */}
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/instagram.png" alt="Instagram" className="w-8 h-8" />
              <div>
                <span className="text-base font-semibold text-slate-900">Instagram Radar</span>
                <div className="text-[11px] text-slate-500">Track & analyze accounts</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search..." className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors w-40" />
              <button className="px-3 py-1.5 text-sm rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800 transition-colors" onClick={() => { setIsAddOpen(true); setErrorMsg(''); setNewUsername(''); }}>+ Add</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-4 py-2.5 text-left text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '35%' }}>Account</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '12%' }}>Posts</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '17%' }}>Followers</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '17%' }}>Following</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-medium text-slate-500 uppercase tracking-wider" style={{ width: '14%' }}>Eng. Rate</th>
                  <th className="px-4 py-2.5" style={{ width: '5%' }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-400">No accounts yet. Click "+ Add" to start tracking.</td></tr>}
                {filtered.map((account) => {
                  const isSelected = selectedId === account.id;
                  return (
                    <tr key={account.id} className={`cursor-pointer transition-colors border-b border-slate-50 last:border-0 ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/60'}`} onClick={() => selectAccount(account)}>
                      <td className="px-4 py-2.5 text-sm align-middle">
                        <div className="flex items-center gap-3">
                          {account.profile_pic_url ? (
                            <img src={proxyImg(account.profile_pic_url)} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-semibold">{(account.username || '?')[0].toUpperCase()}</div>
                          )}
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium truncate text-slate-900">{account.full_name || account.username}</span>
                              {account.is_verified && <svg className="w-3.5 h-3.5 text-sky-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>}
                            </div>
                            <div className="text-[11px] text-slate-400 truncate">@{account.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{format(account.media_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{format(account.follower_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right font-medium tabular-nums text-slate-700">{format(account.following_count)}</td>
                      <td className="px-4 py-2.5 text-sm text-right">
                        {account.engagement_rate != null ? (
                          <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">{account.engagement_rate}%</span>
                        ) : <span className="text-slate-300">-</span>}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right">
                        <button className="inline-flex p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors" title="Remove" onClick={(e) => { e.stopPropagation(); requestDelete(account); }}><Trash2 className="w-3.5 h-3.5" /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ═══ Dashboard ═══ */}
        {selectedId && pd && (
          <div ref={previewRef} className="mt-5 space-y-4">

            {/* Hero Card */}
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-6 py-5">
                <div className="flex flex-col md:flex-row items-start gap-5">
                  {/* Profile pic */}
                  <div className="shrink-0">
                    {selectedAccount?.profile_pic_url ? (
                      <img src={proxyImg(selectedAccount.profile_pic_url)} alt="" className="w-20 h-20 rounded-2xl object-cover ring-1 ring-slate-200" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-500 text-2xl font-bold">{(selectedAccount?.username || '?')[0].toUpperCase()}</div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-lg font-bold text-slate-900">{profile?.full_name || profile?.username}</span>
                      {profile?.is_verified && <svg className="w-4 h-4 text-sky-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" /></svg>}
                      {profile?.is_business && <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-slate-100 text-slate-500">Business</span>}
                      {isLoadingPreview && <span className="text-xs text-slate-400 animate-pulse">Loading...</span>}
                    </div>
                    <a href={`https://instagram.com/${profile?.username}`} target="_blank" rel="noreferrer" className="text-sm text-slate-500 hover:text-slate-700 hover:underline">@{profile?.username}</a>
                    {profile?.biography && <p className="text-sm text-slate-600 mt-2 whitespace-pre-line line-clamp-2 leading-relaxed">{profile.biography}</p>}
                    {profile?.external_url && <a href={profile.external_url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-600 hover:underline mt-1 inline-block">{profile.external_url}</a>}
                  </div>
                  {/* Grade */}
                  <div className="flex flex-col items-center shrink-0">
                    <div className={`w-14 h-14 rounded-xl ${grade.bg} border ${grade.border} flex items-center justify-center`}>
                      <span className={`text-xl font-black ${grade.color}`}>{grade.grade}</span>
                    </div>
                    <span className="text-[10px] text-slate-500 mt-1.5 font-medium">{grade.label}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Followers</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.follower_count)}</div>
                {growth?.followers?.weekly_change != null && growth.followers.weekly_change !== 0 && (
                  <div className={`text-[10px] font-medium mt-1 ${growth.followers.weekly_change > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {growth.followers.weekly_change > 0 ? '+' : ''}{formatNumber(growth.followers.weekly_change)} this week
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Following</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.following_count)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Posts</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(profile?.media_count)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Shares (synced)</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(stats?.total_shares)}</div>
                <div className="text-[10px] text-slate-400 mt-1">~{formatNumber(stats?.avg_shares)} avg / post</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Engagement Rate</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{stats?.engagement_rate != null ? `${stats.engagement_rate}%` : '-'}</div>
                <div className={`text-[10px] font-medium mt-1 ${grade.color}`}>{grade.label}</div>
              </div>
              <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                <div className="text-[11px] text-slate-500 mb-1">Post Frequency</div>
                <div className="text-xl font-bold text-slate-900 tracking-tight">{frequency.perWeek || '-'}<span className="text-sm font-normal text-slate-400">/wk</span></div>
                <div className="text-[10px] text-slate-400 mt-1">{frequency.perMonth}/month</div>
              </div>
              {earnings ? (
                <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                  <div className="text-[11px] text-slate-500 mb-1">Est. Earnings</div>
                  <div className="text-lg font-bold text-slate-900 tracking-tight">${formatNumber(earnings.min)} <span className="text-slate-300 font-normal">–</span> ${formatNumber(earnings.max)}</div>
                  <div className="text-[10px] text-slate-400 mt-1">per sponsored post</div>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 p-3.5 bg-white">
                  <div className="text-[11px] text-slate-500 mb-1">Avg. Likes</div>
                  <div className="text-xl font-bold text-slate-900 tracking-tight">{formatNumber(stats?.avg_likes)}</div>
                </div>
              )}
            </div>

            {/* Growth Chart */}
            {chartData.length >= 2 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <SectionTitle>Follower Growth</SectionTitle>
                <div className="flex items-end justify-between mb-2 text-[10px] text-slate-400">
                  <span>{formatShortDate(chartData[0]?.date)}</span>
                  <span>{formatShortDate(chartData[chartData.length - 1]?.date)}</span>
                </div>
                <MiniLineChart data={chartData} height={100} color="#0ea5e9" />
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                  <span className="text-[11px] text-slate-400">{chartData.length} days of data</span>
                  {chartData.length >= 2 && (() => {
                    const change = chartData[chartData.length - 1].value - chartData[0].value;
                    return <span className={`text-xs font-semibold ${change >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{change >= 0 ? '+' : ''}{formatNumber(change)} total</span>;
                  })()}
                </div>
              </div>
            )}

            {/* Content Breakdown + Posting Analysis */}
            {content.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <SectionTitle>Content Breakdown</SectionTitle>
                  <div className="space-y-2.5">
                    {breakdown.map(b => (
                      <div key={b.key} className="flex items-center gap-2.5">
                        <span className="text-[11px] text-slate-600 w-16 shrink-0 font-medium">{b.label}</span>
                        <div className="flex-1 h-4 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${b.bg}`} style={{ width: `${Math.max(4, b.pct)}%` }} />
                        </div>
                        <span className="text-[11px] text-slate-500 w-20 text-right tabular-nums">{b.pct}% ({b.count})</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 h-2 rounded-full overflow-hidden flex">
                    {breakdown.map(b => <div key={b.key} className={`${b.bg}`} style={{ width: `${b.pct}%` }} title={`${b.label}: ${b.pct}%`} />)}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-5">
                  <SectionTitle>Posting Analysis</SectionTitle>
                  <PostingHoursChart hours={postingHours} />
                  <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Weekly</span>
                      <span className="font-medium text-slate-800">{frequency.perWeek} posts/week</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-500">Monthly</span>
                      <span className="font-medium text-slate-800">{frequency.perMonth} posts/month</span>
                    </div>
                    {bestHours.length > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-slate-500">Best Hours</span>
                        <span className="font-medium text-slate-800">{bestHours.map(h => `${String(h.hour).padStart(2, '0')}:00`).join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Top Performing Posts */}
            {topPosts.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white p-5">
                <SectionTitle>Top Performing Posts</SectionTitle>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {topPosts.map((post, i) => (
                    <button key={post.code || i} onClick={() => setPostPopup(post)} className="flex-shrink-0 w-36 group text-left">
                      <div className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 mb-1.5">
                        {post.thumbnail_url ? <img src={proxyImg(post.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} /> : null}
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold bg-white/90 text-slate-700">#{i + 1}</div>
                        {(post.product_type === 'clips' || post.media_type === 2) && <svg className="absolute top-1.5 right-1.5 w-4 h-4 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-600">
                        <span>&#9829; {formatNumber(post.like_count)}</span>
                        <span>&#128172; {formatNumber(post.comment_count)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500">
                        {post.play_count != null ? <span>&#9654; {formatNumber(post.play_count)}</span> : null}
                        <span title="Shares (API)">&#8599; {post.share_count != null ? formatNumber(post.share_count) : '—'}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* All Content */}
            {content.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-5 pt-5 pb-3 flex items-center justify-between">
                  <SectionTitle>All Content ({content.length})</SectionTitle>
                  <div className="flex items-center bg-slate-100 rounded-lg p-0.5">
                    <button onClick={() => setViewMode('grid')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Grid</button>
                    <button onClick={() => setViewMode('table')} className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'}`}>Table</button>
                  </div>
                </div>
                {viewMode === 'grid' && (
                  <div className="px-5 pb-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {content.map((c, i) => (
                        <button key={c.code || i} onClick={() => setPostPopup(c)} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden group block text-left">
                          {c.thumbnail_url ? <img src={proxyImg(c.thumbnail_url)} alt="" className="w-full h-full object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.img-fallback')?.classList.remove('hidden'); }} /> : null}
                          <div className={`img-fallback absolute inset-0 flex items-center justify-center bg-slate-100 text-slate-400 text-xs ${c.thumbnail_url ? 'hidden' : ''}`}>No image</div>
                          <div className="absolute top-1.5 right-1.5">
                            {(c.product_type === 'clips' || c.media_type === 2) && <svg className="w-4 h-4 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>}
                            {c.media_type === 8 && <svg className="w-4 h-4 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24"><path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" /></svg>}
                          </div>
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center px-1">
                            <div className="flex flex-col items-center gap-1 text-white text-[10px] sm:text-xs font-bold text-center leading-tight">
                              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
                                <span>&#9829; {formatNumber(c.like_count)}</span>
                                <span>&#128172; {formatNumber(c.comment_count)}</span>
                              </div>
                              <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
                                {c.play_count != null ? <span>&#9654; {formatNumber(c.play_count)}</span> : null}
                                <span title="Shares">&#8599; {c.share_count != null ? formatNumber(c.share_count) : '—'}</span>
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {viewMode === 'table' && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead>
                        <tr className="border-y border-slate-100">
                          <th className="px-3 py-2 text-left font-medium text-slate-500">#</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-500 w-12">Preview</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-500">Type</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-500 min-w-[220px]">Caption</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Likes</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Comments</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Plays</th>
                          <th className="px-3 py-2 text-right font-medium text-slate-500">Shares</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-500">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {content.map((c, i) => {
                          const mt = mediaTypeLabel(c.media_type, c.product_type);
                          return (
                            <tr key={c.code || i} className="border-t border-slate-50 hover:bg-slate-50/60 cursor-pointer" onClick={() => setPostPopup(c)}>
                              <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                              <td className="px-3 py-2">{c.thumbnail_url ? <img src={proxyImg(c.thumbnail_url)} alt="" className="w-10 h-10 rounded object-cover" loading="lazy" onError={e => { e.target.style.display = 'none'; }} /> : <div className="w-10 h-10 rounded bg-slate-100" />}</td>
                              <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${mt.color}`}>{mt.text}</span></td>
                              <td className="px-3 py-2 text-slate-700 max-w-[280px]"><div className="truncate" title={c.caption}>{c.caption || '-'}</div></td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatNumber(c.like_count)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatNumber(c.comment_count)}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{c.play_count ? formatNumber(c.play_count) : '-'}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{c.share_count != null ? formatNumber(c.share_count) : '—'}</td>
                              <td className="px-3 py-2 text-slate-600 whitespace-nowrap"><div>{formatDate(c.taken_at)}</div><div className="text-[10px] text-slate-400">{timeAgo(c.taken_at)}</div></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {content.length === 0 && !isLoadingPreview && (
              <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center text-sm text-slate-400">
                No posts stored yet. Posts will be synced automatically tonight.
              </div>
            )}
          </div>
        )}
      </div>

      {/* ═══ Post Popup ═══ */}
      {postPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPostPopup(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Instagram-style header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
              {profile?.profile_pic_url ? (
                <img src={proxyImg(profile.profile_pic_url)} alt="" className="w-9 h-9 rounded-full object-cover ring-2 ring-pink-100" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm font-semibold text-slate-900 truncate">{profile?.username || 'instagram'}</span>
                  {profile?.is_verified && <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>}
                </div>
                <div className="text-[11px] text-slate-400">{timeAgo(postPopup.taken_at)}</div>
              </div>
              {(() => { const mt = mediaTypeLabel(postPopup.media_type, postPopup.product_type); return <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${mt.color}`}>{mt.text}</span>; })()}
              <button className="p-1 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setPostPopup(null)}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Post image / video */}
            <div className="bg-black flex items-center justify-center" style={{ minHeight: 320 }}>
              {postPopup.video_url ? (
                <video src={proxyImg(postPopup.video_url)} poster={postPopup.thumbnail_url ? proxyImg(postPopup.thumbnail_url) : undefined} controls autoPlay playsInline className="w-full max-h-[480px] object-contain" />
              ) : postPopup.thumbnail_url ? (
                <img src={proxyImg(postPopup.thumbnail_url)} alt="" className="w-full max-h-[480px] object-contain" onError={e => { e.target.style.display = 'none'; }} />
              ) : <div className="text-slate-500 text-sm py-20">No preview</div>}
            </div>

            {/* Action icons row (Instagram style) */}
            <div className="flex items-center gap-4 px-4 pt-3 pb-1">
              <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
              <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
              {postPopup.play_count != null && <svg className="w-6 h-6 text-slate-800" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            </div>

            {/* Metrics */}
            <div className="px-4 pb-2">
              <div className="flex items-center gap-4 text-sm">
                <span className="font-bold text-slate-900">{formatNumber(postPopup.like_count)} <span className="font-normal text-slate-500">likes</span></span>
                <span className="font-bold text-slate-900">{formatNumber(postPopup.comment_count)} <span className="font-normal text-slate-500">comments</span></span>
                {postPopup.play_count != null && <span className="font-bold text-slate-900">{formatNumber(postPopup.play_count)} <span className="font-normal text-slate-500">plays</span></span>}
                {postPopup.share_count != null && <span className="font-bold text-slate-900">{formatNumber(postPopup.share_count)} <span className="font-normal text-slate-500">shares</span></span>}
              </div>
            </div>

            {/* Scrollable bottom area: caption + date + link */}
            <div className="flex-1 overflow-y-auto px-4 pb-4" style={{ maxHeight: 160 }}>
              {postPopup.caption && (
                <p className="text-sm text-slate-700 leading-relaxed mb-2">
                  <span className="font-semibold text-slate-900 mr-1.5">{profile?.username}</span>
                  {postPopup.caption}
                </p>
              )}
              <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-3">
                {formatDate(postPopup.taken_at)}
                {postPopup.video_duration ? <span className="ml-2">{Math.round(postPopup.video_duration)}s</span> : null}
              </div>
              <a href={postPopup.url || `https://www.instagram.com/p/${postPopup.code}/`} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 text-white text-sm font-medium hover:opacity-90 transition-opacity">
                <InstagramIcon className="w-4 h-4" /> Open on Instagram
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Add Modal ═══ */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setIsAddOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg mx-4">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">Add Instagram Account</h3>
              <button className="text-slate-400 hover:text-slate-600 transition-colors" onClick={() => setIsAddOpen(false)}>&#10005;</button>
            </div>
            <div className="p-5 space-y-3">
              <input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAccount()} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 focus:bg-white focus:border-slate-300 outline-none transition-colors" placeholder="Username, @handle, or instagram.com/username" autoFocus />
              {errorMsg ? <div className="text-xs text-red-600">{errorMsg}</div> : null}
              <div className="flex justify-end gap-2 pt-2">
                <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 transition-colors" onClick={() => setIsAddOpen(false)}>Cancel</button>
                <button className="px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" onClick={addAccount} disabled={isSaving || !newUsername.trim() || !getBrandId()}>{isSaving ? 'Adding...' : 'Add Account'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Delete Modal ═══ */}
      {deleteState.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })} />
          <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md mx-4 p-5">
            <div className="text-base font-semibold text-slate-900 mb-2">Remove Account</div>
            {deleteState.step === 1 ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">You're about to remove "@{deleteState.account?.username}" from this brand. Continue?</p>
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium" onClick={() => setDeleteState(s => ({ ...s, step: 2 }))}>Continue</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Are you sure? This won't delete the account from the library — it only removes access for this brand.</p>
                {errorMsg ? <div className="text-xs text-red-600">{errorMsg}</div> : null}
                <div className="flex justify-end gap-2 pt-2">
                  <button className="px-3 py-2 rounded-lg border border-slate-200 text-sm" onClick={() => setDeleteState({ open: false, step: 1, account: null })}>Cancel</button>
                  <button className="px-3 py-2 rounded-lg bg-red-600 text-white text-sm font-medium disabled:opacity-50" disabled={isSaving} onClick={performDelete}>{isSaving ? 'Removing...' : 'Remove'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
